import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import syncService from '../services/sync';
import Header from './Header';
import Footer from './Footer';
import CookieConsent from './CookieConsent';

const Layout = ({ children }) => {
  const location = useLocation();
  const [syncStatus, setSyncStatus] = useState('idle'); // 'idle', 'syncing', 'synced', 'error', 'offline'
  const [showSyncBar, setShowSyncBar] = useState(false);


  useEffect(() => {
    // Vérifier l'état de connexion
    const updateOnlineStatus = () => {
      if (!navigator.onLine) {
        setSyncStatus('offline');
        setShowSyncBar(true);
      } else {
        setSyncStatus('idle');
        setShowSyncBar(false);
      }
    };

    updateOnlineStatus();
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);

    // Démarrer la synchronisation (seulement si nécessaire)
    const startSync = async () => {
      if (!navigator.onLine) {
        setSyncStatus('offline');
        setShowSyncBar(true);
        return;
      }

      // Ne pas synchroniser si déjà en cours
      if (syncStatus === 'syncing') {
        return;
      }

      setSyncStatus('syncing');
      setShowSyncBar(true);
      
      try {
        await syncService.sync();
        setSyncStatus('synced');
        
        // Masquer la barre après 2 secondes
        setTimeout(() => {
          setShowSyncBar(false);
          setSyncStatus('idle');
        }, 2000);
      } catch (error) {
        // Ne pas afficher d'erreur si c'est juste une absence de changements ou si pas de données à synchroniser
        if (error.response?.status !== 204 && 
            error.response?.status !== 404 && 
            error.message !== 'No pending changes to sync') {
          console.error('Erreur synchronisation:', error);
          // Ne pas afficher l'erreur si c'est juste qu'il n'y a rien à synchroniser
          if (error.response?.status !== 400) {
            setSyncStatus('error');
            setTimeout(() => {
              setShowSyncBar(false);
              setSyncStatus('idle');
            }, 3000);
          } else {
            // 400 = pas de changements à synchroniser, c'est normal
            setSyncStatus('synced');
            setTimeout(() => {
              setShowSyncBar(false);
              setSyncStatus('idle');
            }, 1000);
          }
        } else {
          // 204 = pas de changements, c'est normal
          setSyncStatus('idle');
          setShowSyncBar(false);
        }
      }
    };

    // Sync initiale (seulement si connecté)
    if (navigator.onLine) {
      startSync();
    }

    // Sync périodique (toutes les 2 minutes au lieu de 30 secondes pour réduire les requêtes)
    let isMounted = true;
    const syncInterval = setInterval(() => {
      if (navigator.onLine && isMounted) {
        startSync();
      }
    }, 120000); // 2 minutes au lieu de 30 secondes

    return () => {
      isMounted = false;
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
      clearInterval(syncInterval);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Skip link pour navigation au clavier */}
      <a href="#main-content" className="skip-link">
        Aller au contenu principal
      </a>
      
      {/* Header avec carrousel et navbar */}
      <Header />

      {/* Barre de synchronisation (si nécessaire) */}
      {showSyncBar && (
        <div className="sticky top-16 z-40 animate-fade-in" role="status" aria-live="polite" aria-atomic="true">
          {syncStatus === 'syncing' && (
            <div className="bg-blue-100 border-b border-blue-300 px-4 py-2">
              <div className="max-w-7xl mx-auto flex items-center">
                <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse" aria-hidden="true"></div>
                <span className="text-sm text-blue-700 font-medium">Synchronisation en cours...</span>
              </div>
            </div>
          )}
          {syncStatus === 'synced' && (
            <div className="bg-green-100 border-b border-green-300 px-4 py-2">
              <div className="max-w-7xl mx-auto flex items-center">
                <div className="w-2 h-2 bg-green-500 rounded-full mr-2" aria-hidden="true"></div>
                <span className="text-sm text-green-700 font-medium">Synchronisé</span>
              </div>
            </div>
          )}
          {syncStatus === 'error' && (
            <div className="bg-red-100 border-b border-red-300 px-4 py-2" role="alert">
              <div className="max-w-7xl mx-auto flex items-center">
                <div className="w-2 h-2 bg-red-500 rounded-full mr-2" aria-hidden="true"></div>
                <span className="text-sm text-red-700 font-medium">Erreur de synchronisation</span>
              </div>
            </div>
          )}
          {syncStatus === 'offline' && (
            <div className="bg-gray-200 border-b border-gray-400 px-4 py-2" role="status">
              <div className="max-w-7xl mx-auto flex items-center">
                <div className="w-2 h-2 bg-gray-500 rounded-full mr-2" aria-hidden="true"></div>
                <span className="text-sm text-gray-700 font-medium">Mode hors ligne</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Content - Style SaaS moderne */}
      <main id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 min-h-[calc(100vh-500px)] pt-20" role="main">
        {children}
      </main>

      {/* Footer */}
      <Footer />

      {/* Popup d'acceptation des cookies - Affiché systématiquement */}
      <CookieConsent />
    </div>
  );
};

export default Layout;
